import React, { useState, useEffect } from 'react';
import apiService from '../../../../services/apiService';
import {
  Card,
  Row,
  Col,
  Typography,
  Avatar,
  Tag,
  Space,
  Tabs,
  Table,
  Statistic,
  Descriptions,
  Spin,
  Empty,
  Result
} from 'antd';
import {
  BuildOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  ProjectOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

/**
 * Everything below is read from /customers/:id. Nothing on this screen is
 * invented.
 *
 * It used to be: the four figures across the top (Annual Billing, Total Unpaid,
 * Total Value) were Math.random(), redrawn on every visit, and the Recent
 * Activity, Orders and Payments lists were a fixed sample written into the file
 * — the same two orders and three invoices against whichever real customer you
 * happened to open. Nobody ever saw them, because the screen crashed before it
 * could draw (see the null guard further down), but fixing the crash would have
 * put invented balances in front of a real customer's name.
 *
 * The endpoint already returns the five most recent orders, quotations and
 * invoices along with full counts, so the screen is built from those instead.
 */
interface RecentDoc {
  id: string;
  number: string;
  totalAmount: number | string | null;
  status: string;
  createdAt: string;
}

interface Customer {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  counts: { salesOrders: number; quotations: number; invoices: number };
  recentOrders: RecentDoc[];
  recentQuotations: RecentDoc[];
  recentInvoices: RecentDoc[];
}

interface CustomerProfileProps {
  customerId?: string;
  onClose?: () => void;
}

const money = (amount: number | string | null) => {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'HKD',
    minimumFractionDigits: 2
  }).format(n);
};

const statusColour = (status: string) => {
  const s = (status || '').toUpperCase();
  if (['PAID', 'COMPLETED', 'DELIVERED', 'CONFIRMED', 'ACCEPTED'].includes(s)) return 'green';
  if (['DRAFT', 'PENDING', 'SENT'].includes(s)) return 'orange';
  if (['CANCELLED', 'REJECTED', 'OVERDUE'].includes(s)) return 'red';
  return 'default';
};

const hasAddress = (a: Customer['address']) =>
  !!(a.street || a.city || a.state || a.zipCode || a.country);

const CustomerProfile: React.FC<CustomerProfileProps> = ({ customerId }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    const fetchCustomer = async () => {
      if (!customerId) {
        setFailed('No customer was selected.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setFailed(null);
        const data: any = await apiService.get(`/customers/${customerId}`);
        const payload = data?.data ?? data;

        const docs = (rows: any[] | undefined, numberField: string): RecentDoc[] =>
          (rows || []).map(r => ({
            id: r.id,
            number: r[numberField] || '-',
            totalAmount: r.totalAmount ?? null,
            status: r.status || '',
            createdAt: r.createdAt
          }));

        setCustomer({
          id: payload.id,
          name: payload.name,
          company: payload.company?.name || '',
          email: payload.email || '',
          phone: payload.phone || '',
          isActive: payload.isActive !== false,
          createdAt: payload.createdAt,
          updatedAt: payload.updatedAt,
          address: {
            street: payload.addressLine1 || payload.address || '',
            city: payload.city || '',
            state: payload.state || '',
            zipCode: payload.postalCode || '',
            country: payload.country || ''
          },
          counts: {
            salesOrders: payload._count?.salesOrders ?? 0,
            quotations: payload._count?.quotations ?? 0,
            invoices: payload._count?.invoices ?? 0
          },
          recentOrders: docs(payload.salesOrders, 'orderNo'),
          recentQuotations: docs(payload.quotations, 'quotationNo'),
          recentInvoices: docs(payload.invoices, 'invoiceNo')
        });
      } catch (error: any) {
        console.error('Error fetching customer:', error);
        // Say so, rather than quietly showing someone else's numbers. The old
        // version fell back to the built-in sample customer here, so a failed
        // request looked like a successful one.
        setFailed(error?.message || 'The customer could not be loaded.');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomer();
  }, [customerId]);

  const docColumns = (numberTitle: string): ColumnsType<RecentDoc> => [
    {
      title: numberTitle,
      dataIndex: 'number',
      key: 'number',
      render: (v: string) => <Text strong>{v}</Text>
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (d: string) => (d ? new Date(d).toLocaleDateString() : '-')
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: (v: number | string | null) => money(v)
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => (s ? <Tag color={statusColour(s)}>{s.toUpperCase()}</Tag> : '-')
    }
  ];

  // The screen used to read customer.name straight away while customer was
  // still null, so it threw on its very first render — every time, for every
  // customer. That is the white screen. These two guards are the fix.
  if (loading) {
    return (
      <div style={{ padding: 80, textAlign: 'center' }}>
        <Spin size="large" tip="Loading customer..." />
      </div>
    );
  }

  if (failed || !customer) {
    return (
      <Result
        status="warning"
        title="This customer could not be shown"
        subTitle={failed || 'No customer data was returned.'}
      />
    );
  }

  const recentDocsPanel = (
    title: string,
    rows: RecentDoc[],
    numberTitle: string,
    total: number
  ) => (
    <Card
      title={
        <Space>
          <span>{title}</span>
          <Tag>{total} in total</Tag>
        </Space>
      }
    >
      {rows.length > 0 ? (
        <>
          <Table
            columns={docColumns(numberTitle)}
            dataSource={rows}
            rowKey="id"
            pagination={false}
            size="small"
          />
          {total > rows.length && (
            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              Showing the {rows.length} most recent of {total}.
            </Text>
          )}
        </>
      ) : (
        <Empty description={`No ${numberTitle.toLowerCase().replace(' no.', '')}s for this customer yet`} />
      )}
    </Card>
  );

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Space size="large" align="start">
          <Avatar size={80} style={{ backgroundColor: '#1890ff' }} icon={<BuildOutlined />} />
          <div>
            <Title level={2} style={{ margin: 0 }}>
              {customer.name}
            </Title>
            <Space>
              <Tag color={customer.isActive ? 'green' : 'default'}>
                {customer.isActive ? 'ACTIVE' : 'INACTIVE'}
              </Tag>
              {customer.company && <Tag color="blue">{customer.company}</Tag>}
            </Space>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                <MailOutlined /> {customer.email || 'No email on record'}
                {'  |  '}
                <PhoneOutlined /> {customer.phone || 'No phone on record'}
              </Text>
            </div>
          </div>
        </Space>
      </Card>

      {/* Counts straight from the record — no estimates, no placeholders */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Invoices"
              value={customer.counts.invoices}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Sales Orders"
              value={customer.counts.salesOrders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Quotations"
              value={customer.counts.quotations}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Customer Since"
              value={
                customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '-'
              }
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large">
          <TabPane tab="Dashboard" key="dashboard">
            {/* Stacks on a narrow window rather than squeezing both columns */}
            <Row gutter={24}>
              <Col xs={24} lg={16}>
                <div style={{ marginBottom: 24 }}>
                  {recentDocsPanel(
                    'Recent Invoices',
                    customer.recentInvoices,
                    'Invoice No.',
                    customer.counts.invoices
                  )}
                </div>
                {recentDocsPanel(
                  'Recent Sales Orders',
                  customer.recentOrders,
                  'Order No.',
                  customer.counts.salesOrders
                )}
              </Col>
              <Col xs={24} lg={8} style={{ marginTop: 0 }}>
                <Card title="Customer Information">
                  <Descriptions column={1} size="small">
                    <Descriptions.Item label="Company">
                      {customer.company || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Email">{customer.email || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{customer.phone || '-'}</Descriptions.Item>
                    <Descriptions.Item label="Customer Since">
                      {customer.createdAt
                        ? new Date(customer.createdAt).toLocaleDateString()
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Last Updated">
                      {customer.updatedAt
                        ? new Date(customer.updatedAt).toLocaleDateString()
                        : '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label="Address">
                      {hasAddress(customer.address) ? (
                        <>
                          {customer.address.street}
                          {customer.address.street && <br />}
                          {[customer.address.city, customer.address.state]
                            .filter(Boolean)
                            .join(', ')}
                          <br />
                          {[customer.address.zipCode, customer.address.country]
                            .filter(Boolean)
                            .join(', ')}
                        </>
                      ) : (
                        '-'
                      )}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </TabPane>

          <TabPane tab="Contact & Address" key="contact">
            <Descriptions title="Contact Information" bordered column={2}>
              <Descriptions.Item label="Customer Name">{customer.name}</Descriptions.Item>
              <Descriptions.Item label="Company">{customer.company || '-'}</Descriptions.Item>
              <Descriptions.Item label="Email">{customer.email || '-'}</Descriptions.Item>
              <Descriptions.Item label="Phone">{customer.phone || '-'}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {customer.isActive ? 'Active' : 'Inactive'}
              </Descriptions.Item>
              <Descriptions.Item label="Customer Since">
                {customer.createdAt ? new Date(customer.createdAt).toLocaleDateString() : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>
                {hasAddress(customer.address) ? (
                  <>
                    {customer.address.street}
                    {customer.address.street && <br />}
                    {[customer.address.city, customer.address.state, customer.address.zipCode]
                      .filter(Boolean)
                      .join(', ')}
                    <br />
                    {customer.address.country}
                  </>
                ) : (
                  'No address on record'
                )}
              </Descriptions.Item>
            </Descriptions>
          </TabPane>

          <TabPane tab="Invoices" key="invoices">
            {recentDocsPanel(
              'Invoices',
              customer.recentInvoices,
              'Invoice No.',
              customer.counts.invoices
            )}
          </TabPane>

          <TabPane tab="Orders" key="orders">
            {recentDocsPanel(
              'Sales Orders',
              customer.recentOrders,
              'Order No.',
              customer.counts.salesOrders
            )}
          </TabPane>

          <TabPane tab="Quotations" key="quotations">
            {recentDocsPanel(
              'Quotations',
              customer.recentQuotations,
              'Quotation No.',
              customer.counts.quotations
            )}
          </TabPane>

          <TabPane tab="Support" key="support">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ToolOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
              <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
                Support Tickets
              </Title>
              <Text type="secondary">Support ticket management coming soon</Text>
            </div>
          </TabPane>

          <TabPane tab="Projects" key="projects">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <ProjectOutlined style={{ fontSize: '48px', color: '#d9d9d9' }} />
              <Title level={4} style={{ marginTop: '16px', color: '#8c8c8c' }}>
                Projects
              </Title>
              <Text type="secondary">Project management coming soon</Text>
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default CustomerProfile;
